import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import Icon from './Icon'
import type { BillingPeriod } from './LandingPage'
import OtpInput from './OtpInput'
import './AuthPage.css'

type OAuthProvider = 'google' | 'linkedin_oidc' | 'facebook'

interface OAuthButtonConfig {
  provider: OAuthProvider
  label: string
  icon: 'google' | 'linkedin' | 'facebook'
  className: string
  style?: React.CSSProperties
}

const OAUTH_BUTTONS: OAuthButtonConfig[] = [
  {
    provider: 'google',
    label: 'Continue with Google',
    icon: 'google',
    className: 'bg-white shadow-sm hover:shadow-md transition-shadow',
    style: { border: '1px solid var(--auth-line)', color: 'var(--auth-ink)' },
  },
  {
    provider: 'linkedin_oidc',
    label: 'Continue with LinkedIn',
    icon: 'linkedin',
    className: 'text-white shadow-sm hover:brightness-95 transition',
    style: { backgroundColor: '#0A66C2' },
  },
  {
    provider: 'facebook',
    label: 'Continue with Facebook',
    icon: 'facebook',
    className: 'text-white shadow-sm hover:brightness-95 transition',
    style: { backgroundColor: '#1877F2' },
  },
]

const EmailIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)

export const AuthPage = () => {
  const [searchParams] = useSearchParams()
  const pendingProPlan = searchParams.get('plan') as BillingPeriod | null

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isDisabled = loading || oauthLoading || verifying

  // Cleanup cooldown timer
  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
  }, [])

  const startCooldown = useCallback(() => {
    setResendCooldown(30)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  const getRedirectTo = useCallback(() => {
    const baseUrl = `${window.location.origin}/login`
    return pendingProPlan ? `${baseUrl}#plan=${pendingProPlan}` : baseUrl
  }, [pendingProPlan])

  const handleSendOtp = async (e?: FormEvent) => {
    e?.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      })
      if (otpError) throw otpError
      setOtpSent(true)
      setOtpCode('')
      startCooldown()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setVerifying(true)
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      })
      if (verifyError) throw verifyError
      // Session is now active — onAuthStateChange fires automatically
      if (pendingProPlan) {
        window.location.hash = `#plan=${pendingProPlan}`
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code')
    } finally {
      setVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return
    await handleSendOtp()
  }

  const handleOAuthSignIn = useCallback(async (provider: OAuthProvider) => {
    setError(null)
    setOauthLoading(true)
    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: getRedirectTo() },
      })
      if (signInError) throw signInError
    } catch (err: unknown) {
      const providerName = provider === 'linkedin_oidc' ? 'LinkedIn' : provider.charAt(0).toUpperCase() + provider.slice(1)
      setError(err instanceof Error ? err.message : `Failed to sign in with ${providerName}`)
    } finally {
      setOauthLoading(false)
    }
  }, [getRedirectTo])

  if (otpSent) {
    return (
      <div className="auth-page min-h-screen flex">
        <LeftColumn />
        <div className="min-w-0 flex-1 flex items-center justify-center px-4 py-8 sm:p-8 lg:p-12">
          <div className="auth-card w-full max-w-md space-y-6">
            <div className="text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full auth-highlight">
                <EmailIcon className="auth-accent h-8 w-8" />
              </div>
              <h1 className="auth-heading text-2xl font-bold">Check your email</h1>
              <p className="auth-muted">
                We sent a 6-digit code to <span className="font-semibold auth-ink break-words">{email}</span>
              </p>
              <p className="text-sm auth-muted">
                Enter the code below to sign in. The code expires in 10 minutes.
              </p>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <OtpInput appearance="brand" value={otpCode} onChange={setOtpCode} disabled={verifying} />
              {error && (
                <p role="alert" className="auth-error text-sm text-center">{error}</p>
              )}
              <button
                type="submit"
                disabled={verifying || otpCode.replace(/ /g, '').length < 6}
                className="auth-primary flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-medium"
              >
                {verifying ? 'Verifying...' : 'Verify code'}
              </button>
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendCooldown > 0 || loading}
                className="auth-link text-sm"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't receive it? Resend code"}
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setOtpSent(false); setOtpCode(''); setEmail(''); setError(null) }}
              className="auth-secondary w-full rounded-xl border px-4 py-3 text-sm font-medium"
            >
              Use a different email
            </button>
            <BackToHomeLink />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-page min-h-screen flex">
      <LeftColumn />

      <div className="min-w-0 flex-1 flex items-center justify-center px-4 py-8 sm:p-8 lg:p-12">
        <div className="auth-card w-full max-w-md space-y-8">
          {/* Mobile Logo (hidden on lg+) */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-block">
              <img src="/logo-header-black.svg" alt="JobTrackr.it" className="h-10 w-auto" />
            </Link>
          </div>

          <div className="space-y-2">
            <h1 className="auth-heading text-3xl font-bold">Welcome back</h1>
            <p className="auth-muted">Sign in to continue to your dashboard</p>
          </div>

          <div className="space-y-4">
            {/* OAuth Buttons */}
            {OAUTH_BUTTONS.map(({ provider, label, icon, className, style }) => (
              <button
                key={provider}
                type="button"
                onClick={() => handleOAuthSignIn(provider)}
                disabled={isDisabled}
                className={`flex w-full items-center justify-center gap-3 rounded-xl px-4 py-3.5 text-sm font-medium disabled:opacity-60 ${className}`}
                style={style}
              >
                <Icon type={icon} size={20} />
                {label}
              </button>
            ))}

            {/* Divider */}
            <div className="flex items-center gap-4 py-2">
              <div className="auth-divider h-px flex-1" />
              <span className="auth-muted text-xs font-medium uppercase tracking-wider">Or</span>
              <div className="auth-divider h-px flex-1" />
            </div>

            {/* Email OTP Form */}
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="auth-ink text-sm font-medium">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="auth-input w-full rounded-xl border px-4 py-3 text-sm"
                  disabled={isDisabled}
                />
              </div>
              {error && (
                <p role="alert" className="auth-error text-sm">{error}</p>
              )}
              <button
                type="submit"
                disabled={isDisabled}
                className="auth-primary flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-medium"
              >
                <EmailIcon />
                {loading ? 'Sending code...' : 'Continue with Email'}
              </button>
            </form>
          </div>

          <div className="pt-4 text-center">
            <BackToHomeLink />
          </div>
        </div>
      </div>
    </div>
  )
}

const BackToHomeLink = () => (
  <Link
    to="/"
    className="auth-link block text-center text-sm"
  >
    ← Back to home
  </Link>
)

const SOCIAL_PROOF_INITIALS = ['A', 'B', 'C', 'D', 'E']

// Left column with branding and value props
const LeftColumn = () => (
  <div className="auth-brand-panel hidden lg:flex lg:w-[45%] xl:w-[40%] p-12 flex-col items-center justify-between">
    <div className="relative z-10 space-y-6 text-center flex flex-col items-center">
      <Link to="/" className="inline-block">
        <img src="/logo-header-black.svg" alt="JobTrackr.it" className="h-10 w-auto" />
      </Link>

      <div className="space-y-3 pt-4">
        <h2 className="auth-heading text-3xl xl:text-4xl font-bold leading-tight">
          Track your job search<br /><span className="auth-accent">with confidence</span>
        </h2>
        <p className="auth-muted text-base xl:text-lg max-w-md">
          Stay organized, never miss a follow-up, and land your dream job faster.
        </p>
      </div>

      <div className="pt-4">
        <img src="/logo.svg" alt="JobTrackr mascot" className="h-52 w-auto" />
      </div>
    </div>

    {/* Social proof */}
    <div className="relative z-10 space-y-3 pt-8 flex flex-col items-center">
      <div className="flex -space-x-2">
        {SOCIAL_PROOF_INITIALS.map((letter) => (
          <div
            key={letter}
            className="auth-avatar h-9 w-9 rounded-full border-2 flex items-center justify-center text-xs font-medium"
          >
            {letter}
          </div>
        ))}
      </div>
      <p className="auth-muted text-sm">
        Join job seekers who track smarter
      </p>
    </div>
  </div>
)
